/**
 * Content Management → Bulk Infographics Upload
 *
 * Fresh component written alongside ContentMgmtSyllabusBulk, not copied. They
 * share a visual language and stage machine but their classifier + ingest
 * endpoints differ. Key differences:
 *   • Accepts image MIME types only (jpg/jpeg/png/webp).
 *   • Classifier is filename-only (regex) — no LLM call, no PyMuPDF.
 *   • Uses /api/content/bulk-ingest instead of /api/syllabus/bulk-ingest.
 *   • Admin can override board / class / subject / chapter per file — useful
 *     when one admin drops multiple images for the SAME chapter (the classifier
 *     parses each filename independently, so the fields auto-fill).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, API_BASE, authedFetch, chunkedUpload } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ImagePlus, UploadCloud, CheckCircle2, AlertTriangle, XCircle,
  Loader2, Info, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const ACCEPTED_EXT = /\.(jpg|jpeg|png|webp|gif)$/i;

const BOARD_OPTIONS = [
  { value: 'cbse', label: 'CBSE' },
  { value: 'icse', label: 'ICSE / CISCE' },
  { value: 'ib', label: 'IB' },
  { value: 'igcse', label: 'Cambridge IGCSE' },
  { value: 'state_board', label: 'State Board' },
  { value: 'nios', label: 'NIOS' },
];

const CLASS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1), label: `Class ${i + 1}`,
}));

const SUBJECT_FALLBACK = [
  // Core academic
  'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology', 'Biotechnology',
  'English', 'Hindi', 'Sanskrit',
  'Social Science', 'History', 'Geography', 'Civics', 'Economics',
  'Political Science', 'Psychology', 'Sociology',
  // Commerce / Senior secondary electives
  'Accountancy', 'Business Studies', 'Computer Science', 'Informatics Practices',
  // Arts / Vocational / PE (NEP 2020 additions)
  'Arts', 'Fine Art', 'Sangeet', 'Home Science', 'Creative Writing & Translation',
  'Physical Education And Well Being', 'Health & Physical Education',
  'Vocational Education', 'Skills Education', 'The World Around Us',
  // Languages (regional)
  'Kannada', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Bengali',
  // Environmental
  'EVS/Environmental Studies',
].map(s => ({ value: s, label: s }));

const STAGE = {
  PICKED: 'picked',
  CHECKING: 'checking-duplicate',
  DUPLICATE: 'duplicate',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  READY: 'ready',
  INGESTING: 'ingesting',
  INGESTED: 'ingested',
  FAILED: 'failed',
};

function stageBadge(stage, pct) {
  switch (stage) {
    case STAGE.PICKED: return <Badge className="bg-slate-100 text-slate-600 rounded-full text-[10px]">picked</Badge>;
    case STAGE.CHECKING: return <Badge className="bg-slate-100 text-slate-500 rounded-full text-[10px]">checking…</Badge>;
    case STAGE.DUPLICATE: return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px] uppercase tracking-wider">Duplicate</Badge>;
    case STAGE.UPLOADING: return <Badge className="bg-amber-100 text-amber-700 rounded-full text-[10px]" data-testid="cm-info-uploading">uploading… {typeof pct === 'number' && pct > 0 ? `${pct}%` : ''}</Badge>;
    case STAGE.UPLOADED: return <Badge className="bg-sky-100 text-sky-700 rounded-full text-[10px]">uploaded</Badge>;
    case STAGE.READY: return <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px]">ready</Badge>;
    case STAGE.INGESTING: return <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px]">ingesting…</Badge>;
    case STAGE.INGESTED: return <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px] uppercase tracking-wider">Pending review</Badge>;
    case STAGE.FAILED: return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px]">failed</Badge>;
    default: return null;
  }
}

export default function ContentMgmtInfographicsBulk({ onBatchIngested }) {
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);

  // Per (board, class) subject cache — fetched from /api/onboarding/subjects so
  // the dropdown mirrors what the rest of the platform actually knows about.
  const [subjectCache, setSubjectCache] = useState({});   // {"cbse:10": [...]}

  const subjectCacheKey = (b, c) => (b && c ? `${b}:${c}` : '');

  const loadSubjectsFor = async (b, c) => {
    const key = subjectCacheKey(b, c);
    if (!key || subjectCache[key]) return;
    try {
      const r = await apiGet(`/onboarding/subjects?board=${encodeURIComponent(b)}&class_level=${encodeURIComponent(c)}`);
      const arr = (r.subjects || [])
        .map(s => (typeof s === 'string' ? s : s.name))
        .filter(Boolean)
        .map(s => ({ value: s, label: s }));
      setSubjectCache(prev => ({ ...prev, [key]: arr.length ? arr : SUBJECT_FALLBACK }));
    } catch {
      setSubjectCache(prev => ({ ...prev, [key]: SUBJECT_FALLBACK }));
    }
  };

  const subjectsFor = (b, c) => {
    const key = subjectCacheKey(b, c);
    if (!key) return SUBJECT_FALLBACK;
    return subjectCache[key] || SUBJECT_FALLBACK;
  };

  const pick = () => inputRef.current?.click();

  const setRow = (id, patch) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const handleFiles = async (files) => {
    const list = Array.from(files).filter(
      f => f.type.startsWith('image/') || ACCEPTED_EXT.test(f.name)
    );
    if (!list.length) { toast.error('Only image files (.jpg/.jpeg/.png/.webp/.gif) are accepted'); return; }

    const now = Date.now();
    const newRows = list.map((f, i) => ({
      id: `ir_${now}_${i}_${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      name: f.name,
      sizeMb: +(f.size / 1024 / 1024).toFixed(2),
      stage: STAGE.CHECKING,
      error: null,
      fileUrl: null,
      duplicate: false,
      dupExisting: null,
      detected: null,
      board: '',
      classLevel: '',
      subject: '',
      topic: '',
      heading: '',
      contentId: null,
    }));
    setRows(prev => [...prev, ...newRows]);

    // Fire duplicate check + filename classification in parallel.
    try {
      const [dupRes, classifyRes] = await Promise.all([
        apiPost('/content/check-duplicates', {
          filenames: newRows.map(r => r.name),
          content_type: 'infographic',
        }),
        apiPost('/content/bulk-classify', {
          filenames: newRows.map(r => r.name),
          content_type: 'infographic',
        }),
      ]);
      const dupMap = dupRes.duplicates || {};
      const clsByName = {};
      (classifyRes.items || []).forEach(it => { clsByName[it.filename] = it.detected || {}; });

      setRows(prev => prev.map(r => {
        if (!newRows.some(n => n.id === r.id)) return r;
        const dup = dupMap[r.name];
        const detected = clsByName[r.name] || {};
        if (dup) {
          return { ...r, stage: STAGE.DUPLICATE, duplicate: true, dupExisting: dup, detected };
        }
        return {
          ...r, stage: STAGE.PICKED, detected,
          board: detected.board || '',
          classLevel: detected.class_level || '',
          subject: detected.subject || '',
          topic: detected.topic || '',
        };
      }));
      const dupCount = Object.keys(dupMap).length;
      if (dupCount > 0) {
        toast.error(`${dupCount} file${dupCount === 1 ? '' : 's'} already in the library — those will be skipped`);
      }
    } catch {
      setRows(prev => prev.map(r =>
        newRows.some(n => n.id === r.id) && r.stage === STAGE.CHECKING
          ? { ...r, stage: STAGE.PICKED } : r
      ));
    }
  };

  const onPick = (e) => handleFiles(e.target.files);
  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  // Apply one set of (board/class/subject) to ALL selected rows — speeds up the
  // common case of "I'm dropping 20 images for the same chapter".
  const [bulkBoard, setBulkBoard] = useState('');
  const [bulkClass, setBulkClass] = useState('');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkTopic, setBulkTopic] = useState('');

  // Fetch subjects whenever bulk board+class is set, or whenever any row's
  // (board, class) combo changes. Cached so we only hit the endpoint once per
  // unique combination.
  useEffect(() => {
    if (bulkBoard && bulkClass) loadSubjectsFor(bulkBoard, bulkClass);
  }, [bulkBoard, bulkClass]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const combos = new Set();
    rows.forEach(r => { if (r.board && r.classLevel) combos.add(`${r.board}:${r.classLevel}`); });
    combos.forEach(c => {
      const [b, cl] = c.split(':');
      loadSubjectsFor(b, cl);
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyBulkToReady = () => {
    if (!bulkBoard && !bulkClass && !bulkSubject && !bulkTopic) {
      toast.error('Pick at least one field to apply');
      return;
    }
    setRows(prev => prev.map(r => {
      if (r.stage !== STAGE.PICKED && r.stage !== STAGE.READY && r.stage !== STAGE.UPLOADED) return r;
      return {
        ...r,
        board: bulkBoard || r.board,
        classLevel: bulkClass || r.classLevel,
        subject: bulkSubject || r.subject,
        topic: bulkTopic || r.topic,
      };
    }));
    toast.success('Applied to all editable rows');
  };

  // Upload one file to /api/upload/file. On success the row flips to READY.
  // Uses `chunkedUpload` which auto-routes: ≤50 MB through the single-POST
  // fast path; larger files (HD videos, 4K cinegraphics) get split into 5 MB
  // chunks to bypass CDN edge body-size limits. Live progress % is surfaced
  // in the row state so the admin sees "uploading… 73%" instead of a static
  // spinner during a 500 MB MP4 upload.
  const uploadOne = async (row) => {
    if (row.duplicate) return;
    try {
      setRow(row.id, { stage: STAGE.UPLOADING, error: null, uploadPct: 0 });
      const up = await chunkedUpload(row.file, {
        onProgress: (loaded, total) => {
          const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          setRow(row.id, { uploadPct: pct });
        },
      });
      setRow(row.id, { stage: STAGE.READY, fileUrl: up.file_url, uploadPct: 100 });
    } catch (ex) {
      setRow(row.id, { stage: STAGE.FAILED, error: ex?.message || 'upload failed' });
    }
  };

  const uploadAllPending = async () => {
    const pending = rows.filter(r => r.stage === STAGE.PICKED && !r.duplicate);
    if (!pending.length) { toast.error('No new files to upload'); return; }
    for (const r of pending) await uploadOne(r);
  };

  const ingestOne = async (row) => {
    if (!row.board || !row.classLevel || !row.subject) {
      setRow(row.id, { stage: STAGE.FAILED, error: 'board / class / subject required' });
      return { status: 'error' };
    }
    setRow(row.id, { stage: STAGE.INGESTING, error: null });
    try {
      const res = await apiPost('/content/bulk-ingest', {
        items: [{
          file_url: row.fileUrl,
          content_type: 'infographic',
          board: row.board,
          class_level: row.classLevel,
          subject: row.subject,
          topic: row.topic || '',
          heading: row.heading || '',
          title: row.detected?.topic || row.topic || row.name,
          original_filename: row.name,
        }],
      });
      const out = (res.outcomes || [])[0] || {};
      if (out.status === 'ok') {
        setRow(row.id, { stage: STAGE.INGESTED, contentId: out.content_id });
        return { status: 'ok' };
      }
      setRow(row.id, { stage: STAGE.FAILED, error: out.error || 'ingest failed' });
      return { status: 'error' };
    } catch (ex) {
      setRow(row.id, { stage: STAGE.FAILED, error: ex?.message || 'network error' });
      return { status: 'error' };
    }
  };

  const ingestAllReady = async () => {
    const ready = rows.filter(r => r.stage === STAGE.READY);
    if (!ready.length) { toast.error('No ready rows to ingest'); return; }
    setRunning(true);
    let ok = 0, err = 0;
    for (const r of ready) {
      // eslint-disable-next-line no-await-in-loop
      const out = await ingestOne(r);
      if (out.status === 'ok') ok++; else err++;
    }
    setRunning(false);
    if (err === 0) toast.success(`All ${ok} submitted for admin review`);
    else if (ok === 0) toast.error(`Ingestion failed for all ${err} files`);
    else toast.warning(`Submitted ${ok}/${ok + err} — ${err} failed`);
    if (ok > 0 && onBatchIngested) onBatchIngested();
  };

  /**
   * Retry one failed row. Two recovery paths:
   *   - fileUrl is null → upload phase failed → re-run uploadOne
   *   - fileUrl exists → ingest phase failed → re-run ingestOne
   * The admin can also bulk-retry every failed row with the toolbar button.
   */
  const retryRow = async (row) => {
    if (row.duplicate) return 'skip';
    if (!row.fileUrl) {
      await uploadOne(row);
      return 'ok-partial';
    }
    const out = await ingestOne(row);
    return out.status === 'ok' ? 'ok' : 'error';
  };

  const retryAllFailed = async () => {
    const failed = rows.filter(r => r.stage === STAGE.FAILED);
    if (!failed.length) { toast.error('No failed rows to retry'); return; }
    setRunning(true);
    let ok = 0, partial = 0, err = 0;
    for (const r of failed) {
      // eslint-disable-next-line no-await-in-loop
      const result = await retryRow(r);
      if (result === 'ok') ok++;
      else if (result === 'ok-partial') partial++;
      else if (result === 'error') err++;
    }
    setRunning(false);
    const total = ok + partial + err;
    if (err === 0 && partial === 0) {
      toast.success(`Retried ${ok} item(s) successfully`);
      if (ok > 0 && onBatchIngested) onBatchIngested();
    } else if (ok + partial === 0) {
      toast.error(`All ${total} retry attempt(s) failed`);
    } else {
      const parts = [];
      if (ok > 0) parts.push(`${ok} submitted`);
      if (partial > 0) parts.push(`${partial} re-uploaded (click Submit to ingest)`);
      if (err > 0) parts.push(`${err} still failing`);
      toast.warning(`Retried ${total}: ${parts.join(' · ')}`);
      if (ok > 0 && onBatchIngested) onBatchIngested();
    }
  };

  const counts = useMemo(() => ({
    total: rows.length,
    dup: rows.filter(r => r.duplicate).length,
    pending: rows.filter(r => r.stage === STAGE.PICKED && !r.duplicate).length,
    ready: rows.filter(r => r.stage === STAGE.READY).length,
    ingested: rows.filter(r => r.stage === STAGE.INGESTED).length,
    failed: rows.filter(r => r.stage === STAGE.FAILED).length,
  }), [rows]);

  return (
    <div className="space-y-5" data-testid="cm-infographics-bulk">
      <Card className="p-5 rounded-2xl border-slate-100">
        <div className="flex items-center gap-2 mb-1">
          <ImagePlus className="w-4 h-4 text-emerald-500" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Bulk upload infographics</h3>
        </div>
        <p className="text-[11px] text-slate-500 font-body mb-4">
          Drop one or more image files. Filenames following <code className="px-1 py-0.5 rounded bg-slate-100 text-[10px]">Board-Class-Subject-Chapter.jpg</code> are auto-classified. Every submission lands in <strong>Pending Review</strong> and is only shown to students after an admin approves it in the Content Library. Multiple images per chapter is totally fine — use the bulk-apply controls below to set scope on a batch of them at once.
        </p>

        <div
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          onClick={pick}
          className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-8 text-center cursor-pointer hover:bg-slate-50"
          data-testid="cm-infographics-dropzone">
          <UploadCloud className="w-9 h-9 mx-auto text-slate-400 mb-2" />
          <p className="font-heading text-sm text-slate-700">Drop images here or click to choose files</p>
          <p className="text-[10px] text-slate-400 font-body mt-1">Accepts .jpg, .jpeg, .png, .webp, .gif</p>
          <input
            ref={inputRef} type="file" accept="image/*" multiple
            onChange={onPick} className="hidden"
            data-testid="cm-infographics-file-input"
          />
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-[10px] font-heading uppercase tracking-wider">
              <Badge className="bg-slate-100 text-slate-700 rounded-full">Total: {counts.total}</Badge>
              {counts.dup > 0 && <Badge className="bg-rose-100 text-rose-700 rounded-full">Duplicates: {counts.dup}</Badge>}
              {counts.pending > 0 && <Badge className="bg-amber-100 text-amber-700 rounded-full">To upload: {counts.pending}</Badge>}
              {counts.ready > 0 && <Badge className="bg-indigo-100 text-indigo-700 rounded-full">Ready: {counts.ready}</Badge>}
              {counts.ingested > 0 && <Badge className="bg-emerald-100 text-emerald-700 rounded-full">Pending review: {counts.ingested}</Badge>}
              {counts.failed > 0 && <Badge className="bg-rose-100 text-rose-700 rounded-full">Failed: {counts.failed}</Badge>}
            </div>

            {/* Bulk-apply controls */}
            <div className="mt-4 rounded-xl bg-slate-50/70 border border-slate-100 p-3">
              <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500 mb-2">Bulk-apply scope to all editable rows</p>
              <div className="flex flex-wrap items-center gap-2">
                <select value={bulkBoard} onChange={e => setBulkBoard(e.target.value)}
                  data-testid="cm-infographics-bulk-board"
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5">
                  <option value="">Board…</option>
                  {BOARD_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
                <select value={bulkClass} onChange={e => setBulkClass(e.target.value)}
                  data-testid="cm-infographics-bulk-class"
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5">
                  <option value="">Class…</option>
                  {CLASS_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={bulkSubject} onChange={e => setBulkSubject(e.target.value)}
                  data-testid="cm-infographics-bulk-subject"
                  disabled={!bulkBoard || !bulkClass}
                  title={(!bulkBoard || !bulkClass) ? 'Pick Board + Class first to filter subjects' : ''}
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 disabled:bg-slate-50 disabled:text-slate-400">
                  <option value="">{(!bulkBoard || !bulkClass) ? 'Pick Board+Class first…' : 'Subject…'}</option>
                  {subjectsFor(bulkBoard, bulkClass).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <input type="text" value={bulkTopic} onChange={e => setBulkTopic(e.target.value)}
                  placeholder="Chapter / topic (optional)"
                  data-testid="cm-infographics-bulk-topic"
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 flex-1 min-w-[180px]"
                />
                <Button onClick={applyBulkToReady} variant="ghost"
                  className="text-xs font-heading text-indigo-600 hover:bg-indigo-50"
                  data-testid="cm-infographics-bulk-apply">Apply to all</Button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={uploadAllPending} disabled={counts.pending === 0 || running}
                data-testid="cm-infographics-upload-all-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full text-xs font-heading disabled:opacity-40">
                Upload files ({counts.pending})
              </Button>
              <Button onClick={ingestAllReady} disabled={counts.ready === 0 || running}
                data-testid="cm-infographics-ingest-all-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
                {running ? 'Submitting…' : `Submit ${counts.ready} for review`}
              </Button>
              {counts.failed > 0 && (
                <Button onClick={retryAllFailed} disabled={running}
                  data-testid="cm-infographics-retry-all-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
                  <RotateCcw className="w-3 h-3 mr-1.5" />
                  Retry {counts.failed} failed
                </Button>
              )}
              <Button onClick={() => setRows([])} disabled={running} variant="ghost"
                className="text-slate-500 text-xs font-heading disabled:opacity-40"
                data-testid="cm-infographics-clear-btn">Clear list</Button>
            </div>
          </>
        )}
      </Card>

      {rows.length > 0 && (
        <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="cm-infographics-rows">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Filename</th>
                  <th className="text-left px-3 py-2">Board</th>
                  <th className="text-left px-3 py-2">Class</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Chapter</th>
                  <th className="text-center px-3 py-2">Stage</th>
                  <th className="text-center px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const locked = r.stage === STAGE.UPLOADING
                    || r.stage === STAGE.INGESTING
                    || r.stage === STAGE.INGESTED
                    || r.stage === STAGE.DUPLICATE
                    || r.stage === STAGE.CHECKING;
                  return (
                    <tr key={r.id}
                      className={`border-t border-slate-100 ${r.duplicate ? 'bg-rose-50/50' : r.stage === STAGE.FAILED ? 'bg-rose-50/30' : r.stage === STAGE.INGESTED ? 'bg-emerald-50/30' : ''}`}
                      data-testid={`cm-info-row-${idx}`}>
                      <td className="px-3 py-2 max-w-[280px]">
                        <p className="font-body text-slate-800 break-all leading-snug" title={r.name}>{r.name}</p>
                        <p className="text-[10px] text-slate-400 font-body mt-0.5">{r.sizeMb} MB</p>
                        {r.duplicate && r.dupExisting && (
                          <p className="text-[10px] text-rose-600 font-body mt-1">
                            Uploaded on {r.dupExisting.created_at?.slice(0, 10) || '—'}
                            {r.dupExisting.topic ? ` · ${r.dupExisting.topic}` : ''}
                          </p>
                        )}
                        {r.error && (
                          <p className="text-[10px] text-rose-600 font-body mt-1" title={r.error}>
                            <AlertTriangle className="inline w-3 h-3 mr-0.5" />{r.error}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 min-w-[110px]">
                        <select disabled={locked} value={r.board}
                          onChange={e => setRow(r.id, { board: e.target.value })}
                          data-testid={`cm-info-row-board-${idx}`}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50">
                          <option value="">—</option>
                          {BOARD_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 min-w-[90px]">
                        <select disabled={locked} value={r.classLevel}
                          onChange={e => setRow(r.id, { classLevel: e.target.value })}
                          data-testid={`cm-info-row-class-${idx}`}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50">
                          <option value="">—</option>
                          {CLASS_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 min-w-[140px]">
                        <select disabled={locked || !r.board || !r.classLevel} value={r.subject}
                          onChange={e => setRow(r.id, { subject: e.target.value })}
                          data-testid={`cm-info-row-subject-${idx}`}
                          title={(!r.board || !r.classLevel) ? 'Pick Board + Class first' : ''}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50 disabled:text-slate-400">
                          <option value="">—</option>
                          {subjectsFor(r.board, r.classLevel).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 min-w-[160px]">
                        <input disabled={locked} type="text" value={r.topic}
                          onChange={e => setRow(r.id, { topic: e.target.value })}
                          placeholder="chapter / topic"
                          data-testid={`cm-info-row-topic-${idx}`}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50" />
                      </td>
                      <td className="px-3 py-2 text-center" data-testid={`cm-info-row-stage-${idx}`}>
                        <div className="flex items-center justify-center gap-1.5">
                          {r.stage === STAGE.INGESTED && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                          {r.stage === STAGE.FAILED && <AlertTriangle className="w-4 h-4 text-rose-500" />}
                          {(r.stage === STAGE.UPLOADING || r.stage === STAGE.INGESTING || r.stage === STAGE.CHECKING) && (
                            <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                          )}
                          {stageBadge(r.stage, r.uploadPct)}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.stage === STAGE.FAILED && (
                            <button onClick={() => retryRow(r)} disabled={running}
                              className="text-amber-500 hover:text-amber-600 disabled:opacity-40"
                              title="Retry this file"
                              data-testid={`cm-info-row-retry-${idx}`}>
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => removeRow(r.id)} disabled={running}
                            className="text-slate-400 hover:text-rose-500"
                            title="Remove from list"
                            data-testid={`cm-info-row-remove-${idx}`}>
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-500 font-body">
            <Info className="w-3 h-3" />
            Files are uploaded and ingested one at a time so each HTTP request stays under the production proxy timeout. Approve the pending-review submissions in the Syllabus Library tab (same library powers infographics review, filtered by type).
          </div>
        </Card>
      )}
    </div>
  );
}
